/**
 * The deployment's own settings, which nginx serves beside the bundle from the container's
 * environment. Where none are served, every switch is at its default: quiet, strict, and with
 * the experimental panels shown.
 */

import { z } from 'zod';

import type { SentryConfig } from '@/types/monitoring';

import { fetchServedFile } from './servedFile';

const RUNTIME_CONFIG_URL = '/runtime-config.json';

const RuntimeConfigSchema = z.object({
    loudSchemaErrors: z.boolean(),
    strictMediaServer: z.boolean(),
    experimentalRuleFlows: z.boolean(),
    experimentalAiSearch: z.boolean(),
    sentryDsn: z.string(),
    sentryEnvironment: z.string(),
});

/** Whether this deployment shows contract violations on screen: its LOUD_SCHEMA_ERRORS. */
export async function fetchLoudSchemaErrors(): Promise<boolean> {
    const config = await fetchServedFile(RUNTIME_CONFIG_URL, RuntimeConfigSchema);
    return config === null ? false : config.loudSchemaErrors;
}

/** Whether this deployment refuses a media_server's data that breaks its schema. */
export async function fetchStrictMediaServer(): Promise<boolean> {
    const config = await fetchServedFile(RUNTIME_CONFIG_URL, RuntimeConfigSchema);
    return config === null ? true : config.strictMediaServer;
}

/** Whether this deployment offers the rule flows panels: its EXPERIMENTAL_RULE_FLOWS. */
export async function fetchExperimentalRuleFlows(): Promise<boolean> {
    const config = await fetchServedFile(RUNTIME_CONFIG_URL, RuntimeConfigSchema);
    return config === null ? true : config.experimentalRuleFlows;
}

/** Whether this deployment offers the AI Search panel: its EXPERIMENTAL_AI_SEARCH. */
export async function fetchExperimentalAiSearch(): Promise<boolean> {
    const config = await fetchServedFile(RUNTIME_CONFIG_URL, RuntimeConfigSchema);
    return config === null ? true : config.experimentalAiSearch;
}

/** Where this deployment sends errors, or null where it sets no SENTRY_DSN. */
export async function fetchSentryConfig(): Promise<SentryConfig | null> {
    const config = await fetchServedFile(RUNTIME_CONFIG_URL, RuntimeConfigSchema);
    if (config === null || config.sentryDsn === '') {
        return null;
    }
    return config.sentryEnvironment === ''
        ? { dsn: config.sentryDsn }
        : { dsn: config.sentryDsn, environment: config.sentryEnvironment };
}
